import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { fetchShopItems, addShopItem, deleteShopItem, useShopItemsListener } from "../logic/AdminShopLogic";
import './AdminDashboard.css';

const AdminShopManager = () => {
  const navigate = useNavigate();

  const [selectedCollection, setSelectedCollection] = useState("shopItems-v2");
  const [shopItems, setShopItems] = useState([]);
  const [editStates, setEditStates] = useState({});
  const [newItem, setNewItem] = useState({
    docId: "",
    name: "",
    description: "",
    priceDP: 0,
    stock: 0,
    imageUrl: ""
  });

  // Real-time shop items listener
  useShopItemsListener(selectedCollection, setShopItems);

  // Add item
  const sanitizeForDocId = (text) =>
    text.toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/--+/g, '-').replace(/^-+|-+$/g, '');

  const handleAddItem = async () => {
    const { docId, name, description, priceDP, stock, imageUrl } = newItem;
    if (!docId || !name || !description || !imageUrl || !priceDP || stock < 0) {
      alert("All fields are required.");
      return;
    }

    await addShopItem({ name, description, priceDP, stock, imageUrl }, docId, selectedCollection);
    setNewItem({ docId: "", name: "", description: "", priceDP: 0, stock: 0, imageUrl: "" });
    // Real-time listener will automatically update the items
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      await deleteShopItem(id, selectedCollection);
      alert("✅ Item deleted!");
      // Real-time listener will automatically update the items
    } catch (err) {
      console.error("Error deleting item:", err);
      alert("❌ Failed to delete item.");
    }
  };

  const handleSaveAllItems = async () => {
    try {
      const updates = Object.entries(editStates);
      if (updates.length === 0) {
        alert('✅ No changes to save!');
        return;
      }

      for (const [itemId, edited] of updates) {
        const original = shopItems.find(item => item.id === itemId);
        if (!original) continue;

        const priceDP = edited.priceDP !== undefined ? edited.priceDP : original.priceDP;
        const stock = edited.stock !== undefined ? edited.stock : original.stock;
        const imageUrl = edited.imageUrl !== undefined ? edited.imageUrl : original.imageUrl;
        const name = edited.name !== undefined ? edited.name : original.name;
        const description = edited.description !== undefined ? edited.description : original.description;

        const itemRef = doc(db, selectedCollection, itemId);
        await updateDoc(itemRef, { name, description, priceDP, stock, imageUrl });
      }

      alert('✅ All changes saved!');
      setEditStates({});
      // Real-time listener will automatically update the items
    } catch (err) {
      console.error(err);
      alert('❌ Failed to save all changes: ' + err.message);
    }
  };


  const handleSaveItem = async (itemId) => {
    const edited = editStates[itemId] || {};
    const original = shopItems.find(item => item.id === itemId);
    if (!original) return;

    const priceDP = edited.priceDP !== undefined ? edited.priceDP : original.priceDP;
    const imageUrl = edited.imageUrl !== undefined ? edited.imageUrl : original.imageUrl;
    const stock = edited.stock !== undefined ? edited.stock : original.stock;
    const name = edited.name !== undefined ? edited.name : original.name;
    const description = edited.description !== undefined ? edited.description : original.description;
    const itemRef = doc(db, selectedCollection, itemId);
    try {
      await updateDoc(itemRef, { name, description, priceDP, stock, imageUrl });
      alert('✅ Item updated!');
      setEditStates({});
      // Real-time listener will automatically update the items
    } catch (err) {
      alert('❌ Failed to update: ' + err.message);
    }
  };

  return (
    <div className="admin-container">
      <h2>🛒 Manage Shop Items</h2>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button onClick={() => navigate('/admin-panelx-952504')}>⬅️ Back to Dashboard</button>
        <button onClick={() => navigate('/admin/dp-purchase-history')}>💰 DP Purchase History</button>
      </div>

      <div className="shop-item-form">
        <div className="field">
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            className="collection-selector"
          >
            <option value="shopItems-v2">OCG</option>
            <option value="shopItemsTCG-v2">TCG</option>
            <option value="shopItemsAccess-v2">Collection</option>
            <option value="shopItemsMisc-v2">Miscellaneous</option>
          </select>
          <label>Document ID</label>
          <input
            value={newItem.docId}
            onChange={(e) => setNewItem({ ...newItem, docId: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Name</label>
          <input
            value={newItem.name}
            onChange={(e) => {
              const name = e.target.value;
              setNewItem({ ...newItem, name, docId: sanitizeForDocId(name) });
            }}
          />
        </div>
        <div className="field">
          <label>Description</label>
          <input
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
          />
        </div>
        <div className="field">
          <label>DP</label>
          <input
            type="number"
            value={newItem.priceDP}
            onChange={(e) => setNewItem({ ...newItem, priceDP: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>Stock</label>
          <input
            type="number"
            value={newItem.stock}
            onChange={(e) => setNewItem({ ...newItem, stock: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label>Image URL</label>
          <input
            value={newItem.imageUrl}
            onChange={(e) => setNewItem({ ...newItem, imageUrl: e.target.value })}
          />
        </div>
        <div className="field button-field">
          <button onClick={handleAddItem}>Add Item</button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <h2>📝 Update Shop Items</h2>
        <button onClick={handleSaveAllItems}>💾 Save All</button>
      </div>

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>DP</th>
            <th>Stock</th>
            <th>Image URL</th>
            <th>Delete</th>
          </tr>
        </thead>
        <tbody>
          {shopItems.map((item) => (
            <tr key={item.id}>
              <td>
                <input
                  type="text"
                  className="name-input"

                  value={editStates[item.id]?.name ?? item.name}
                  onChange={(e) =>
                    setEditStates({
                      ...editStates,
                      [item.id]: {
                        ...editStates[item.id],
                        name: e.target.value
                      }
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  value={editStates[item.id]?.description ?? item.description}
                  className="description-input"

                  onChange={(e) =>
                    setEditStates({
                      ...editStates,
                      [item.id]: {
                        ...editStates[item.id],
                        description: e.target.value
                      }
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="dp-input"

                  value={editStates[item.id]?.priceDP ?? item.priceDP}
                  onChange={(e) =>
                    setEditStates({
                      ...editStates,
                      [item.id]: {
                        ...editStates[item.id],
                        priceDP: Number(e.target.value)
                      }
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="stock-input"

                  value={editStates[item.id]?.stock ?? item.stock}
                  onChange={(e) =>
                    setEditStates({
                      ...editStates,
                      [item.id]: {
                        ...editStates[item.id],
                        stock: Number(e.target.value)
                      }
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="text"
                  className="image-url-input"

                  value={editStates[item.id]?.imageUrl ?? item.imageUrl}
                  onChange={(e) =>
                    setEditStates({
                      ...editStates,
                      [item.id]: {
                        ...editStates[item.id],
                        imageUrl: e.target.value
                      }
                    })
                  }
                />
              </td>
              <td>
                <button onClick={() => handleDeleteItem(item.id)}>🗑️</button>
              </td>
              <td>
                <button onClick={() => handleSaveItem(item.id)}>Save</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminShopManager;
